/**
 * UNI-Q173 — **「설정」 한 곳.** 사용자: *"이팩트 끄고 키는 기능을 좀 넣어줘 '설정' 이라는 메뉴 하나 만들어서..
 * 자동물약, 반격 등등 이런거 싹다 몰아줘"*.
 *
 * 켜고 끄는 것이 가방(물품·장비·능력)·도움말·하단 [더보기]에 흩어져 있었고, 도움말 세 곳은 그 위치를
 * 틀리게 안내했다. 여기 모으되 **그리는 코드는 원래 화면과 같은 함수**를 부른다 — 복사하면 한쪽이 낡는다.
 * 원래 자리에서도 그대로 바꿀 수 있다(익숙한 곳을 빼앗지 않는다).
 *
 * 기기 설정(연출·목표 줄·HUD·프레임)은 모두 이 기기에 저장된다 — 줄마다 되풀이하지 않는다.
 * 새로 생긴 것은 둘이다: 자동 공격·강공을 카드로 바꾸는 자리, **타격 연출 켬끔**(`core/fx-pref.js`).
 */
import { sec } from './common.js';
import { S } from '../../core/state.js';
import { fxOff } from '../../core/fx-pref.js';
import { goalLineOff } from '../../core/goal-pref.js';
import { hudFxOff } from '../../core/hud-fx-pref.js';
import { frameMeterOn } from '../../core/frame-pref.js';
import { firstHintDone } from '../first-hint.js'; // 묶음 HUD1 — 첫 조작 자리 힌트 켬끔(이 기기)
import { audioSettings } from '../../core/sound.js';
import { readabilityControls } from '../readability.js';
import { qualityControls, getQualityPreference, getQuality } from '../../render/quality.js';
import { notifyState } from '../notify.js';
import { worldNotifyState } from '../world-notify.js';
import { autoActions, autoBreakdown, autoStatsRow } from './inv.js';
import { audioSec, notifySec, worldPushSec } from './help.js';

/**
 * 묶음 UI2b(DESIGN 14.439) — **조작이 먼저.** 한 줄 = 이름·짧은 설명(왼쪽) + 상태를 쓴 스위치(오른쪽). 전에는 카드마다 상태 글자와
 * 「꺼짐 — 누르면 켠다」 버튼이 같은 말을 두 번 했고, 버튼마다 「하단 [자동] 버튼과 같은 설정이다」 문단이 붙었다(2차 평론 N8).
 * 덧붙일 말(저장 위치·운영 스위치)은 `note` — 짧은 한 줄만.
 */
const onOff = (on) => (on ? '켜짐' : '꺼짐');
const toggle = (on, onclick, label = onOff(on)) =>
  `<button class="set-switch${on ? ' sel' : ''}" role="switch" aria-checked="${on ? 'true' : 'false'}" onclick="${onclick}">${label}</button>`;
const card = (title, sub, state, body) =>
  `<div class="auto-card set-row"><span class="set-label"><b>${title}</b><small>${sub}</small></span>${body}</div>`;
const note = (text) => `<small class="set-note">${text}</small>`;

function combat(y) {
  const auto = !!y.auto, force = !!S.forceMode;
  return sec('전투',
    '<div class="auto-grid set-list">'
    // V1d(14.481) — C10(14.478) 새 동작을 가르친다: 켜면 사거리 안 → 없으면 근처(탐색 반경 · 화면 안)로 걸어감 · 표적이 죽으면 켬끔과 무관하게 이어 친다
    // 2026-10-21 — 설명은 한 줄. 「꺼도 이어 친다」(14.478)는 남긴다 — 끈 사람이 놀라는 동작이다
    + card('자동 공격', '사거리 안 → 근처 몬스터 순으로 찾아 친다 · 꺼도 표적이 죽으면 이어 친다', onOff(auto),
      toggle(auto, 'act.autoAttack()'))
    + card('강공', '클릭이 곧 공격 · A · Ctrl+클릭 · 마을에 들면 꺼짐', onOff(force),
      toggle(force, 'act.forceMode()'))
    + card('스킬 자동 사용', '스킬마다 따로 켜고 끈다', '스킬 탭',
      `<button class="set-switch" onclick="act.openPanel('skills')">스킬 탭</button>`)
    + '</div>');
}

function screen(y) {
  const mine = !fxOff();
  // 개인 설정이 켜져 있는데 연출이 없으면 운영 스위치가 꺼 둔 것이다(개인 설정은 0 만 덮는다)
  const byServer = mine && !y.hitFeel;
  return sec('화면',
    readabilityControls() + sec('화질', qualityControls())
    + card('타격 연출', '섬광·파편·흔들림·튀는 숫자 — 꺼도 타격선·데미지는 보인다', mine ? (byServer ? '운영에서 꺼 둠' : '켜짐') : '꺼짐',
      toggle(mine, `act.fxEffects(${mine ? 'false' : 'true'})`, byServer ? '운영에서 꺼 둠' : onOff(mine))
      + (byServer ? note('지금은 운영 설정으로 모든 사람의 연출이 꺼져 있다.') : ''))
    // R184 — 「FPS 가 낮아 보인다」를 기기마다 숫자로 받는 자리. 꺼져 있으면 아무것도 재지 않는다.
    + card('프레임 시간 표시', '끊겨 보일 때 켜고 화면 왼쪽 숫자를 알려 달라', frameMeterOn() ? '켜짐' : '꺼짐',
      toggle(frameMeterOn(), `act.frameMeter(${frameMeterOn() ? 'false' : 'true'})`))
    // UNI-Q179·Q180 — 미니맵 아래 목표 줄(첫 걸음 → 오늘의 목표). 숨겨도 진행·보상은 서버에 그대로 남는다.
    + card('목표 줄', '미니맵 아래 한 줄 — 숨겨도 진행·보상은 그대로', goalLineOff() ? '숨김' : '보임',
      toggle(!goalLineOff(), `act.goalLine(${goalLineOff() ? 'true' : 'false'})`, goalLineOff() ? '숨김' : '보임'))
    + card('전투 HUD', '대상 프레임·보스 체력줄·보상 글자·구역 제목·파티 프레임', hudFxOff() ? '꺼짐' : '켜짐',
      toggle(!hudFxOff(), `act.hudFxToggle(${hudFxOff() ? 'true' : 'false'})`))
    // 묶음 HUD1(DESIGN 14.468) — 처음 10초 동안 가장 가까운 몬스터 위 작은 고리 + 「누르면 공격」. 첫 타격 뒤에는 저절로 꺼진다
    + card('첫 조작 힌트', '처음 10초 몬스터 위 고리 · 첫 타격 뒤 저절로 꺼진다', firstHintDone() ? '꺼짐' : '켜짐',
      toggle(!firstHintDone(), `act.firstHint(${firstHintDone() ? 'true' : 'false'})`)));
}

export function render({ y, c, at }) {
  const audio = audioSettings(), push = worldNotifyState();
  const notification = push.busy ? '상태 확인 중' : push.subscribed ? '닫힌 탭에서도 받음' : notifyState() === 'on' ? '열린 탭에서 받음' : notifyState() === 'denied' ? '브라우저에서 차단됨' : '받지 않음';
  let h = '<section id="settings-control">' + combat(y) + autoActions(y, c, at) + '</section>';
  h += '<details class="ux-details" id="settings-screen"><summary>화면 · 글자·화질·연출</summary>' + screen(y) + '</details>';
  h += '<details class="ux-details" id="settings-audio"><summary>소리 · ' + onOff(audio.on) + '</summary>' + audioSec() + '</details>';
  h += '<details class="ux-details" id="settings-growth"><summary>성장·정리 · 자동 능력치·장비 분해</summary>' + sec('성장', autoStatsRow(y)) + autoBreakdown(y) + '</details>';
  h += '<details class="ux-details" id="settings-notify"><summary>알림 · ' + notification + '</summary>' + notifySec() + worldPushSec() + '</details>';
  return h;
}

/**
 * 재렌더 서명의 설정 몫 — 이 화면만 보는 값(기기 설정·강공). 가방에 저장되는 자동 설정은
 * 공용 서명에 들어 있다(가방 화면도 같은 값을 그린다).
 */
export function stateKey(y) {
  const a = audioSettings();
  const push = worldNotifyState();
  return [
    y.auto ? 1 : 0, y.hitFeel ? 1 : 0, fxOff() ? 1 : 0, goalLineOff() ? 1 : 0, hudFxOff() ? 1 : 0, frameMeterOn() ? 1 : 0, S.forceMode ? 1 : 0, firstHintDone() ? 1 : 0,
    a.on ? 1 : 0, a.swell ? 1 : 0, notifyState(), push.subscribed ? 1 : 0, push.busy ? 1 : 0, push.enabled ? 1 : 0,
    getQualityPreference(), getQuality(),
    // UNI-Q187 ③ — 닫힌 탭 알림 종류(새 세계·오늘의 목표·탑 주간 결과) 켬끔
    push.kindsReady ? 1 : 0, push.kinds?.world ? 1 : 0, push.kinds?.daily ? 1 : 0, push.kinds?.tower ? 1 : 0,
  ].join(',');
}
